import { Box, Image } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

export type MorphingBrandLogoProps = {
  /** URL o data URL del icono de marca */
  iconSrc: string;
  alt: string;
  /** Si es false, se detiene la animación requestAnimationFrame */
  isActive?: boolean;
};

/**
 * Logo con animación tipo “morphing” (misma idea que el splash PWA): escala, rotación y brillo suaves.
 */
export default function MorphingBrandLogo({
  iconSrc,
  alt,
  isActive = true,
}: MorphingBrandLogoProps) {
  const [hasError, setHasError] = useState(false);
  const logoRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const resolvedIcon = hasError ? '' : String(iconSrc || '').trim();

  useEffect(() => {
    if (!isActive || !logoRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    startTimeRef.current = performance.now();

    const animate = (currentTime: number) => {
      if (!logoRef.current || !startTimeRef.current) return;

      const elapsed = currentTime - startTimeRef.current;
      const duration = 2000;
      const progress = (elapsed % duration) / duration;

      const scale = 1 + Math.sin(progress * Math.PI * 2) * 0.05;
      const rotate = Math.sin(progress * Math.PI * 2) * 3;
      const blur = Math.abs(Math.sin(progress * Math.PI * 2)) * 2;
      const glow = 0.5 + Math.sin(progress * Math.PI * 2) * 0.3;

      logoRef.current.style.transform = `scale(${scale}) rotate(${rotate}deg)`;
      logoRef.current.style.filter = `blur(${blur}px) brightness(${1 + glow * 0.2})`;
      logoRef.current.style.opacity = String(0.9 + Math.sin(progress * Math.PI * 2) * 0.1);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive]);

  if (!resolvedIcon) {
    return null;
  }

  return (
    <>
      <Box
        ref={logoRef}
        position="relative"
        style={{
          willChange: 'transform, filter, opacity',
          transformOrigin: 'center center',
        }}
      >
        <Image
          src={resolvedIcon}
          alt={alt}
          boxSize={{ base: '120px', md: '160px', lg: '200px' }}
          borderRadius={{ base: '28px', md: '36px', lg: '44px' }}
          style={{
            willChange: 'transform',
            filter: 'drop-shadow(0 0 40px rgba(59, 130, 246, 0.5))',
          }}
          onError={() => setHasError(true)}
        />
        <Box
          position="absolute"
          inset={0}
          borderRadius={{ base: '28px', md: '36px', lg: '44px' }}
          bgGradient="radial(circle, rgba(59, 130, 246, 0.3), transparent 70%)"
          pointerEvents="none"
          style={{
            willChange: 'opacity',
            animation: 'morphingBrandLogoPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          }}
        />
      </Box>
      <style>
        {`
          @keyframes morphingBrandLogoPulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.8; }
          }
        `}
      </style>
    </>
  );
}
